import React, { useState, useEffect, useContext, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import Card from '../Card/Card';
import CardContent from '../Card/CardContent';
import './Schedule.css';
import convenienceApi from '../../common/api/convenience';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { CardScheduleContext } from '../../contexts/CardScheduleContext';
import ScheduleContent from './ScheduleContent';
import logComponent from '../../common/logComponent';

export default function Schedule({ moduleDescription }) {
  const environmentContext = useContext(EnvironmentContext);
  const cardScheduleContext = useContext(CardScheduleContext);
  const [toCalendarDetails, setToCalendarDetails] = useState(false);
  const isMounted = useRef(false);
  const { hasToken, activeUser: user, selectedMonitoredIndividual } = environmentContext;
  const { scheduleVisibleMonth, scheduleSelectedDate } = cardScheduleContext;
  const loadingRef = useRef(false);

  if (logComponent('Schedule')) { console.debug('Schedule rendered'); }

  const setLoadingRef = (el) => {
    if (logComponent('Schedule')) { console.debug('Schedule setLoadingRef'); }
    loadingRef.current = el;
  }

  const setIsMounted = (el) => {
    if (logComponent('Schedule')) { console.debug('Schedule setIsMounted'); }
    isMounted.current = el;
  }
  const mostRecentRequest = useRef(null);

  const setMostRecentRequest = (el) => {
    if (logComponent('Schedule')) { console.debug('Schedule setMostRecentRequest'); }
    mostRecentRequest.current = el;
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('Schedule')) { console.debug('Schedule onMount'); }
    setIsMounted(true);
    if (!hasToken) return;

    return () => {
      setIsMounted(false);
    };

  }, []);

  useEffect(() => {
    if (logComponent('Schedule')) { console.debug('Schedule useEffect [environmentContext.hasToken]'); }
    if (!hasToken) return;

    loadEvents();
  }, [environmentContext.hasToken]);

  useEffect(() => {
    if (logComponent('Schedule')) { console.debug('Schedule useEffect [environmentContext.selectedMonitoredIndividual, environmentContext.activeUser]'); }
    if (!hasToken) return;

    const { setScheduleVisibleMonth } = cardScheduleContext;
    setScheduleVisibleMonth(new Date());

  }, [environmentContext.selectedMonitoredIndividual, environmentContext.activeUser]);

  useEffect(() => {
    if (logComponent('Schedule')) { console.debug('Schedule useEffect [scheduleVisibleMonth]'); }
    if (!hasToken) return;

    if (scheduleVisibleMonth)
      loadEvents();

  }, [scheduleVisibleMonth]);

  const setVisibleMonthThis = (newMonth) => {
    if (logComponent('Schedule')) { console.debug('Schedule setVisibleMonthThis'); }
    const { setScheduleVisibleMonth } = cardScheduleContext;
    setScheduleVisibleMonth(newMonth);

  }

  const loadEvents = () => {
    if (logComponent('Schedule')) { console.debug('Schedule loadEvents'); }
    if (!hasToken || loadingRef.current) return;
    setLoadingRef(true);
    const { setSchedule, setScheduleLoading, setScheduleError } = cardScheduleContext;

    const timestamp = Date.now();
    setSchedule(null);
    setScheduleLoading(true);
    setScheduleError(false);
    setMostRecentRequest(timestamp);

    convenienceApi.getEventCalendarData(user, selectedMonitoredIndividual, scheduleVisibleMonth)
      .then(
        result => {
          if (!isMounted.current) return; // this prevents data loading when component has already unmounted
          if (timestamp === mostRecentRequest.current) {
            setSchedule(result);
            setScheduleLoading(false);
            setScheduleError(false);
            setMostRecentRequest(null);
            setLoadingRef(false);
          }
        },
        error => {
          if (!isMounted.current) return;
          if (timestamp === mostRecentRequest.current) {
            setSchedule(null);
            setScheduleLoading(false);
            setScheduleError(true);
            setMostRecentRequest(null);
            setLoadingRef(false);
          }
        }
      );
  }

  const handleDateClick = (date) => {
    if (logComponent('Schedule')) { console.debug('Schedule handleDateClick'); }
    setToCalendarDetails(true);
    const { setScheduleVisibleMonth } = cardScheduleContext;
    const { setScheduleSelectedDate } = cardScheduleContext;
    setScheduleVisibleMonth(date);
    setScheduleSelectedDate(date);
  }


  if (toCalendarDetails) {
    return (
      user.groupTypeString === 'Facility' ?
        (selectedMonitoredIndividual ?
          <Navigate
            to={{
              pathname: `/monitoredindividual/${selectedMonitoredIndividual.monitoredIndividualId}/calendar`,

            }} state={{ visibleDate: scheduleVisibleMonth, selectedDate: scheduleSelectedDate }} /> :
          <Navigate
            to={{
              pathname: '/calendar',

            }} state={{ visibleDate: scheduleVisibleMonth, selectedDate: scheduleSelectedDate }} />) :
        user.groupTypeString === 'Family' ?
          <Navigate
            to={{
              pathname: '/calendar',
            }} state={{visibleDate: scheduleVisibleMonth, selectedDate: scheduleSelectedDate }}/> :
          null
    )
  }

  return (
    <Card className="Schedule" title="Event calendar" moduleDescription={moduleDescription} icon="calendar-alt" >
      <CardContent>
        <ScheduleContent reloadAction={loadEvents} setVisibleMonth={setVisibleMonthThis} onDateClick={handleDateClick} />
      </CardContent>
    </Card>
  );
}
